/**
 * DJI KML/WPML 转换工具类
 * 支持生成和解析DJI无人机航点任务文件
 */

import JSZip from 'jszip'
import { BooleanEnum, BtnLoading, ExecuteRCLostActionType, FINISH_ACTION_TYPE, FLY_TO_WAYLINE_MODEL_TYPE, FOCUS_MODE_TYPE, FollowBadType, GIMBAL_HEADERING_TYPE, GIMBAL_PITCH_MODE_TYPE, GIMBAL_ROTATE_MODE_TYPE, METERING_MODE_TYPE, RETURN_MODE_TYPE, SCANNING_MODE_TYPE, TEMPLATE_TYPE, WaylineActionType, WaylineMapType, WaylineSmoothType, WaylineTypeMap } from '../types/wayline'
import { DroneSubEnumValue, PAYLOAD_TRY_TYPE, PayloadSubEnumValue2 } from '../types/device'
import { GetNewPoint, GetPoint } from './kml'

// 枚举和类型定义
export enum FlyToWaylineMode {
  SAFELY = 'safely',
  POINT_TO_POINT = 'pointToPoint'
}

export enum FinishAction {
  GO_HOME = 'goHome',
  NO_ACTION = 'noAction',
  AUTO_LAND = 'autoLand',
  GOTO_FIRST_WAYPOINT = 'gotoFirstWaypoint'
}

export enum ExitOnRCLost {
  GO_CONTINUE = 'goContinue',
  EXECUTE_LOST_ACTION = 'executeLostAction'
}

export enum ExecuteRCLostAction {
  GO_BACK = 'goBack',
  LANDING = 'landing',
  HOVER = 'hover'
}

export enum TemplateType {
  WAYPOINT = 'waypoint',
  MAPPING_2D = 'mapping2d',
  MAPPING_3D = 'mapping3d',
  MAPPING_STRIP = 'mappingStrip'
}

export enum CoordinateMode {
  WGS84 = 'WGS84'
}

export enum HeightMode {
  EGM96 = 'EGM96',
  RELATIVE_TO_START_POINT = 'relativeToStartPoint',
  ABOVE_GROUND_LEVEL = 'aboveGroundLevel',
  REAL_TIME_FOLLOW_SURFACE = 'realTimeFollowSurface'
}

export enum GimbalPitchMode {
  MANUAL = 'manual',
  USE_POINT_SETTING = 'usePointSetting'
}

export enum WaypointHeadingMode {
  FOLLOW_WAYLINE = 'followWayline',
  MANUALLY = 'manually',
  FIXED = 'fixed',
  SMOOTH_TRANSITION = 'smoothTransition',
  TOWARD_POI = 'towardPOI'
}

export enum WaypointHeadingPathMode {
  CLOCKWISE = 'clockwise',
  COUNTER_CLOCKWISE = 'counterClockwise',
  FOLLOW_BAD_ARC = 'followBadArc'
}

export enum WaypointTurnMode {
  COORDINATE_TURN = 'coordinateTurn',
  TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE = 'toPointAndStopWithDiscontinuityCurvature',
  TO_POINT_AND_STOP_WITH_CONTINUITY_CURVATURE = 'toPointAndStopWithContinuityCurvature',
  TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE = 'toPointAndPassWithContinuityCurvature',
  SMOOTH_OVER_POINTS = 'smoothOverPoints'
}

export enum ActionGroupMode {
  SEQUENCE = 'sequence'
}

export enum ActionTriggerType {
  REACH_POINT = 'reachPoint',
  BETWEEN_ADJACENT_POINTS = 'betweenAdjacentPoints',
  MULTIPLE_TIMING = 'multipleTiming',
  MULTIPLE_DISTANCE = 'multipleDistance'
}

export enum ActionActuatorFunc {
  TAKE_PHOTO = 'takePhoto',
  START_RECORD = 'startRecord',
  STOP_RECORD = 'stopRecord',
  FOCUS = 'focus',
  ZOOM = 'zoom',
  GIMBAL_ROTATE = 'gimbalRotate',
  ROTATE_YAW = 'rotateYaw',
  HOVER = 'hover',
  SET_FOCUS_TYPE = 'setFocusType'
}

export enum ImageFormat {
  ZOOM = 'zoom',
  WIDE = 'wide',
  IR = 'ir',
  VISABLE = 'visable'
}

export enum GimbalHeadingYawBase {
  NORTH = 'north',
  AIRCRAFT = 'aircraft'
}

export enum GimbalRotateMode {
  RELATIVE_ANGLE = 'relativeAngle',
  ABSOLUTE_ANGLE = 'absoluteAngle'
}

export enum PayloadEnum {
  M30 = 52,
  M30T = 53,
  M3E = 66,
  M3T = 67
}

// 接口定义
export interface Waypoint {
  index: number;
  longitude: number;
  latitude: number;
  altitude: number;
}

export interface MissionConfig {
  flyToWaylineMode: FlyToWaylineMode;
  finishAction: FinishAction;
  takeOffSecurityHeight: number;
  globalHeight: number;
  autoFlightSpeed: number;
  droneEnumValue: PayloadEnum;
  payloadEnumValue: PayloadEnum;
  waypoints: Waypoint[];
}

export interface ActionParam {
  [key: string]: string | number | boolean;
}

export interface Action {
  wpml_actionId: number;
  wpml_actionActuatorFunc: ActionActuatorFunc;
  wpml_actionActuatorFuncParam: ActionParam;
}

export interface ActionGroup {
  wpml_actionGroupId: number;
  wpml_actionGroupStartIndex: number;
  wpml_actionGroupEndIndex: number;
  wpml_actionGroupMode: ActionGroupMode;
  wpml_actionTrigger: {
    wpml_actionTriggerType: ActionTriggerType;
    wpml_actionTriggerParam?: number;
  };
  wpml_action: Action[];
}

export interface WaypointElement {
  Point: {
    coordinates: string;
  };
  wpml_index: number;
  wpml_ellipsoidHeight?: number;
  wpml_height?: number;
  wpml_waypointSpeed: number;
  wpml_waypointHeadingParam: {
    wpml_waypointHeadingMode: WaypointHeadingMode;
    wpml_waypointHeadingAngle: number;
    wpml_waypointPoiPoint: string;
    wpml_waypointHeadingPathMode: WaypointHeadingPathMode;
    wpml_waypointHeadingPoiIndex: number;
  };
  wpml_waypointTurnParam: {
    wpml_waypointTurnMode: WaypointTurnMode;
    wpml_waypointTurnDampingDist: number;
  };
  wpml_useStraightLine: number;
  wpml_isRisky: number;
  wpml_actionGroup?: ActionGroup[];
}

export interface MissionTemplateInfo {
  wpml_author: string;
  wpml_createTime: number;
  wpml_updateTime: number;
  wpml_missionConfig: {
    wpml_flyToWaylineMode: FlyToWaylineMode;
    wpml_finishAction: FinishAction;
    wpml_exitOnRCLost: ExitOnRCLost;
    wpml_executeRCLostAction: ExecuteRCLostAction;
    wpml_takeOffSecurityHeight: number;
    wpml_globalTransitionalSpeed: number;
    wpml_globalRTHHeight: number;
    wpml_droneInfo: {
      wpml_droneEnumValue: number;
      wpml_droneSubEnumValue: number;
    };
    wpml_payloadInfo: {
      wpml_payloadEnumValue: number;
      wpml_payloadSubEnumValue: number;
      wpml_payloadPositionIndex: number;
    };
  };
  Folder: {
    wpml_templateType: TemplateType;
    wpml_templateId: number;
    wpml_waylineCoordinateSysParam: {
      wpml_coordinateMode: CoordinateMode;
      wpml_heightMode: HeightMode;
      wpml_globalShootHeight: number;
    };
    wpml_autoFlightSpeed: number;
    Placemark: WaypointElement[];
    wpml_payloadParam?: {
      wpml_payloadPositionIndex: number;
      wpml_imageFormat: string;
    };
  }[];
}

export interface MissionWaylinesInfo {
  wpml_missionConfig: MissionTemplateInfo['wpml_missionConfig'];
  Folder: {
    wpml_templateId: number;
    wpml_executeHeightMode: CoordinateMode;
    wpml_waylineId: number;
    wpml_distance: string;
    wpml_duration: string;
    wpml_autoFlightSpeed: number;
    Placemark: WaypointElement[];
  }[];
}

export interface MissionResult {
  templateInfo: MissionTemplateInfo;
  waylinesInfo: MissionWaylinesInfo;
}

// 安全的 hasOwnProperty 检查
function hasOwnProperty<T extends object, K extends PropertyKey> (
  obj: T,
  key: K
): obj is T & Record<K, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

/**
 * DJI KML/WPML 转换工具类
 */
export class DJIWaylineTools {
  /**
   * 解析坐标文本
   * @param text 坐标文本
   * @returns 航点数组
   */
  static parseCoordinates (text: string): Waypoint[] {
    const lines = text.split('\n')
    const waypoints: Waypoint[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue

      const parts = line.split(',')
      if (parts.length < 2) {
        throw new Error(`第${i + 1}行坐标格式不正确: ${line}`)
      }

      const longitude = parseFloat(parts[0])
      const latitude = parseFloat(parts[1])
      const altitude = parts.length > 2 ? parseFloat(parts[2]) : 120

      if (isNaN(longitude) || isNaN(latitude) || isNaN(altitude)) {
        throw new Error(`第${i + 1}行坐标包含非数字值: ${line}`)
      }

      waypoints.push({
        index: i,
        longitude: longitude,
        latitude: latitude,
        altitude: altitude
      })
    }

    return waypoints
  }

  /**
   * 生成带状航线KML/WPML
   * @param config 配置对象
   * @returns 包含templateInfo和waylinesInfo的对象
   */
  static generateStripRoute (config: MissionConfig): MissionResult {
    const templateInfo: MissionTemplateInfo = {
      wpml_author: localStorage.getItem('username') || '',
      wpml_createTime: config.Document.createTime || Date.now(),
      wpml_updateTime: config.Document.updateTime || Date.now(),
      wpml_missionConfig: {
        wpml_flyToWaylineMode: config.Document.missionConfig.flyToWaylineMode || FlyToWaylineMode.SAFELY,
        wpml_finishAction: config.Document.missionConfig.finishAction || FinishAction.GO_HOME,
        wpml_takeOffRefPoint: config.Document.missionConfig.takeOffRefPoint || '',
        wpml_takeOffRefPointAGLHeight: config.Document.missionConfig.takeOffRefPointAGLHeight || 0,
        wpml_exitOnRCLost: ExitOnRCLost.GO_CONTINUE,
        wpml_executeRCLostAction: ExecuteRCLostAction.GO_BACK,
        wpml_takeOffSecurityHeight: config.Document.missionConfig.takeOffSecurityHeight || 20,
        wpml_globalTransitionalSpeed: 15,
        wpml_globalRTHHeight: 100,
        wpml_droneInfo: {
          wpml_droneEnumValue: config.Document.missionConfig.droneInfo.droneEnumValue,
          wpml_droneSubEnumValue: config.Document.missionConfig.droneInfo.droneSubEnumValue
        },
        wpml_payloadInfo: {
          wpml_payloadEnumValue: config.Document.missionConfig.payloadInfo.payloadEnumValue,
          wpml_payloadSubEnumValue: config.Document.missionConfig.payloadInfo.payloadSubEnumValue,
          wpml_payloadPositionIndex: config.Document.missionConfig.payloadInfo.payloadPositionIndex
        }
      },
      Folder: [{
        wpml_templateType: TemplateType.MAPPING_STRIP,
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: CoordinateMode.WGS84,
          wpml_heightMode: HeightMode.RELATIVE_TO_START_POINT,
          wpml_globalShootHeight: config.Document.Folder.globalHeight || 10
        },
        wpml_autoFlightSpeed: config.Document.Folder.autoFlightSpeed || 10,
        Placemark: config.Document.waypoints.map((wp, index) => ({
          Point: {
            coordinates: `${wp.longitude},${wp.latitude}`
          },
          wpml_index: index,
          wpml_ellipsoidHeight: wp.altitude,
          wpml_height: wp.altitude,
          wpml_waypointSpeed: config.Document.Folder.autoFlightSpeed || 10,
          wpml_waypointHeadingParam: {
            wpml_waypointHeadingMode: WaypointHeadingMode.FOLLOW_WAYLINE,
            wpml_waypointHeadingAngle: 0,
            wpml_waypointPoiPoint: new GetPoint().coordinates.toString(),
            wpml_waypointHeadingPathMode: WaypointHeadingPathMode.FOLLOW_BAD_ARC,
            wpml_waypointHeadingPoiIndex: 0
          },
          wpml_waypointTurnParam: {
            wpml_waypointTurnMode: index === 0 || index === config.Document.waypoints.length - 1
              ? WaypointTurnMode.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE
              : WaypointTurnMode.COORDINATE_TURN,
            wpml_waypointTurnDampingDist: index === 0 || index === config.Document.waypoints.length - 1 ? 0 : 0.2
          },
          wpml_useStraightLine: 1,
          wpml_isRisky: 0,
          wpml_actionGroup: index === 0
            ? [{
                wpml_actionGroupId: 0,
                wpml_actionGroupStartIndex: 0,
                wpml_actionGroupEndIndex: config.Document.waypoints.length - 2,
                wpml_actionGroupMode: ActionGroupMode.SEQUENCE,
                wpml_actionTrigger: {
                  wpml_actionTriggerType: ActionTriggerType.MULTIPLE_TIMING,
                  wpml_actionTriggerParam: 7
                },
                wpml_action: [{
                  wpml_actionId: 0,
                  wpml_actionActuatorFunc: ActionActuatorFunc.GIMBAL_ROTATE,
                  wpml_actionActuatorFuncParam: {
                    wpml_gimbalHeadingYawBase: GimbalHeadingYawBase.AIRCRAFT,
                    wpml_gimbalRotateMode: GimbalRotateMode.ABSOLUTE_ANGLE,
                    wpml_gimbalPitchRotateEnable: 1,
                    wpml_gimbalPitchRotateAngle: -90,
                    wpml_gimbalRollRotateEnable: 0,
                    wpml_gimbalRollRotateAngle: 0,
                    wpml_gimbalYawRotateEnable: 1,
                    wpml_gimbalYawRotateAngle: 0,
                    wpml_gimbalRotateTimeEnable: 0,
                    wpml_gimbalRotateTime: 10,
                    wpml_payloadPositionIndex: 0
                  }
                }, {
                  wpml_actionId: 1,
                  wpml_actionActuatorFunc: ActionActuatorFunc.TAKE_PHOTO,
                  wpml_actionActuatorFuncParam: {
                    wpml_payloadPositionIndex: 0,
                    wpml_useGlobalPayloadLensIndex: 0,
                    wpml_payloadLensIndex: 'visable'
                  }
                }]
              }]
            : []
        }))
      }]
    }

    const waylinesInfo: MissionWaylinesInfo = {
      wpml_missionConfig: templateInfo.wpml_missionConfig,
      Folder: [{
        wpml_templateId: 0,
        wpml_executeHeightMode: CoordinateMode.WGS84,
        wpml_waylineId: 0,
        wpml_distance: this.calculateTotalDistance(config.Document.waypoints),
        wpml_duration: this.calculateTotalDuration(config.Document.waypoints, config.Document.Folder.autoFlightSpeed),
        wpml_autoFlightSpeed: config.Document.Folder.autoFlightSpeed,
        Placemark: config.Document.waypoints.map((wp, index) => ({
          Point: {
            coordinates: `${wp.longitude},${wp.latitude}`
          },
          wpml_index: index,
          wpml_executeHeight: wp.altitude,
          wpml_waypointSpeed: config.Document.Folder.autoFlightSpeed || 10
        }))
      }]
    }

    return {
      templateInfo: { Document: templateInfo },
      waylinesInfo: { Document: waylinesInfo }
    }
  }

  static setOther (kmlData:string|number, l?:number) {
    let kml = []
    if (kmlData !== PAYLOAD_TRY_TYPE.M30D && kmlData !== PAYLOAD_TRY_TYPE.M30T) {
      if (kmlData === PAYLOAD_TRY_TYPE.M3T) {
        kml = [{
          wpml_action: [{
            wpml_actionId: 0,
            wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ROTATE,
            wpml_actionActuatorFuncParam: {
              wpml_gimbalHeadingYawBase: GIMBAL_HEADERING_TYPE.AIRCRAFT,
              wpml_gimbalRotateMode: GIMBAL_ROTATE_MODE_TYPE.ABSOLUTE_ANGLE,
              wpml_gimbalPitchRotateEnable: 1,
              wpml_gimbalPitchRotateAngle: l ? -45 : -90,
              wpml_gimbalRollRotateEnable: 0,
              wpml_gimbalRollRotateAngle: 0,
              wpml_gimbalYawRotateEnable: 1,
              wpml_gimbalYawRotateAngle: 0,
              wpml_gimbalRotateTimeEnable: 0,
              wpml_gimbalRotateTime: 10,
              wpml_payloadPositionIndex: 0
            }
          }, {
            wpml_actionId: 1,
            wpml_actionActuatorFunc: WaylineActionType.HOVER,
            wpml_actionActuatorFuncParam: {
              wpml_hoverTime: 1
            }
          }]
        }]
      } else {
        kml = [{
          wpml_action: [{
            wpml_actionId: 0,
            wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ROTATE,
            wpml_actionActuatorFuncParam: {
              wpml_gimbalHeadingYawBase: GIMBAL_HEADERING_TYPE.AIRCRAFT,
              wpml_gimbalRotateMode: GIMBAL_ROTATE_MODE_TYPE.ABSOLUTE_ANGLE,
              wpml_gimbalPitchRotateEnable: 1,
              wpml_gimbalPitchRotateAngle: l ? -45 : -90,
              wpml_gimbalRollRotateEnable: 0,
              wpml_gimbalRollRotateAngle: 0,
              wpml_gimbalYawRotateEnable: 1,
              wpml_gimbalYawRotateAngle: 0,
              wpml_gimbalRotateTimeEnable: 0,
              wpml_gimbalRotateTime: 10,
              wpml_payloadPositionIndex: 0
            }
          }, {
            wpml_actionId: 1,
            wpml_actionActuatorFunc: WaylineActionType.HOVER,
            wpml_actionActuatorFuncParam: {
              wpml_hoverTime: 0.5
            }
          }, {
            wpml_actionId: 2,
            wpml_actionActuatorFunc: WaylineActionType.SET_FOCUS_TYPE,
            wpml_actionActuatorFuncParam: {
              wpml_cameraFocusType: GIMBAL_PITCH_MODE_TYPE.MANUAL,
              wpml_payloadPositionIndex: 0
            }
          }, {
            wpml_actionId: 3,
            wpml_actionActuatorFunc: WaylineActionType.FOCUS,
            wpml_actionActuatorFuncParam: {
              wpml_focusX: 0,
              wpml_focusY: 0,
              wpml_focusRegionWidth: 0,
              wpml_focusRegionHeight: 0,
              wpml_isPointFocus: 0,
              wpml_isInfiniteFocus: 0,
              wpml_payloadPositionIndex: 0,
              wpml_isCalibrationFocus: 0
            }
          }, {
            wpml_actionId: 4,
            wpml_actionActuatorFunc: WaylineActionType.HOVER,
            wpml_actionActuatorFuncParam: {
              wpml_hoverTime: 1
            }
          }]
        }]
      }
    }
    return kml
  }

  static setPolyLineKml (kmlData) {
    const date = new Date().getTime()
    let startPoint = null
    if (kmlData.startPoint) {
      startPoint = new GetNewPoint(kmlData.startPoint.latitude, kmlData.startPoint.longitude, kmlData.startPoint.height).toString()
    }
    const kml = {
      wpml_flyToWaylineMode: kmlData.flyToWaylineMode,
      wpml_finishAction: kmlData.finishAction,
      wpml_exitOnRCLost: ExecuteRCLostActionType.GO_CONTINUE,
      wpml_executeRCLostAction: BtnLoading.GO_BACK,
      wpml_takeOffSecurityHeight: kmlData.takeOffSecurityHeight,
      wpml_takeOffRefPoint: startPoint,
      wpml_takeOffRefPointAGLHeight: 0,
      wpml_globalTransitionalSpeed: 15,
      wpml_globalRTHHeight: 100,
      wpml_droneInfo: {
        wpml_droneEnumValue: kmlData.drone_type,
        wpml_droneSubEnumValue: DroneSubEnumValue[kmlData.payload_types]
      },
      wpml_waylineAvoidLimitAreaMode: 0,
      wpml_payloadInfo: {
        wpml_payloadEnumValue: kmlData.payload_types,
        wpml_payloadSubEnumValue: PayloadSubEnumValue2[kmlData.payload_types],
        wpml_payloadPositionIndex: 0
      }
    }
    const wpml = {
      wpml_author: kmlData.author,
      wpml_createTime: date,
      wpml_updateTime: date,
      wpml_missionConfig: {
        ...kml
      },
      Folder: [{
        wpml_templateType: TEMPLATE_TYPE[kmlData.template_types[0]],
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: WaylineTypeMap.WGS84,
          wpml_heightMode: kmlData.heightMode,
          wpml_globalShootHeight: kmlData.globalHeight,
          wpml_surfaceFollowModeEnable: kmlData.surfaceFollowModeEnable,
          wpml_isRealtimeSurfaceFollow: kmlData.isRealtimeSurfaceFollow,
          wpml_surfaceRelativeHeight: kmlData.globalHeight
        },
        wpml_autoFlightSpeed: kmlData.autoFlightSpeed,
        Placemark: [{
          wpml_caliFlightEnable: 0,
          wpml_elevationOptimizeEnable: 1,
          wpml_shootType: kmlData.shootType,
          wpml_direction: 0,
          wpml_singleLineEnable: 0,
          wpml_cuttingDistance: 1e4,
          wpml_boundaryOptimEnable: 0,
          wpml_leftExtend: 50,
          wpml_rightExtend: 50,
          wpml_includeCenterEnable: 0,
          wpml_efficiencyFlightModeEnable: 0,
          wpml_overlap: {
            wpml_orthoCameraOverlapH: kmlData.orthoLidarOverlapW,
            wpml_orthoCameraOverlapW: kmlData.orthoLidarOverlapW
          },
          LineString: {
            coordinates: []
          },
          wpml_ellipsoidHeight: kmlData.globalHeight,
          wpml_height: kmlData.globalHeight,
          wpml_stripMergedRegions: [],
          wpml_stripUseTemplateAltitude: 0
        }],
        wpml_payloadParam: {
          wpml_payloadPositionIndex: 0,
          wpml_focusMode: FOCUS_MODE_TYPE.FIRST_POINT,
          wpml_meteringMode: METERING_MODE_TYPE.AVERAGE,
          wpml_returnMode: RETURN_MODE_TYPE.SINGLE_RETURN_FIRST,
          wpml_samplingRate: 24e4,
          wpml_scanningMode: SCANNING_MODE_TYPE.REPETITIVE,
          wpml_imageFormat: kmlData.imageFormat.join()
        }
      }]
    }
    const info = {
      wpml_missionConfig: {
        ...kml
      },
      Folder: [{
        wpml_templateId: 0,
        wpml_executeHeightMode: WaylineTypeMap.WGS84,
        wpml_waylineId: 0,
        wpml_distance: kmlData.distance,
        wpml_duration: kmlData.duration,
        wpml_autoFlightSpeed: kmlData.autoFlightSpeed,
        Placemark: []
      }]
    }
    return {
      templateInfo: wpml,
      waylinesInfo: info
    }
  }

  static setPolygonKml (kmlData) {
    const time = new Date().getTime()
    let startPoint = null
    if (kmlData.startPoint) {
      startPoint = new GetNewPoint(kmlData.startPoint.latitude, kmlData.startPoint.longitude, kmlData.startPoint.height).toString()
    }
    const kml = {
      wpml_flyToWaylineMode: kmlData.flyToWaylineMode,
      wpml_finishAction: kmlData.finishAction,
      wpml_exitOnRCLost: ExecuteRCLostActionType.GO_CONTINUE,
      wpml_executeRCLostAction: BtnLoading.GO_BACK,
      wpml_takeOffSecurityHeight: kmlData.takeOffSecurityHeight,
      wpml_takeOffRefPoint: startPoint,
      wpml_takeOffRefPointAGLHeight: 0,
      wpml_globalTransitionalSpeed: 15,
      wpml_globalRTHHeight: 100,
      wpml_droneInfo: {
        wpml_droneEnumValue: kmlData.drone_type,
        wpml_droneSubEnumValue: DroneSubEnumValue[kmlData.payload_types]
      },
      wpml_waylineAvoidLimitAreaMode: 0,
      wpml_payloadInfo: {
        wpml_payloadEnumValue: kmlData.payload_types,
        wpml_payloadSubEnumValue: PayloadSubEnumValue2[kmlData.payload_types],
        wpml_payloadPositionIndex: 0
      }
    }
    const wpml = {
      wpml_author: kmlData.author,
      wpml_createTime: time,
      wpml_updateTime: time,
      wpml_missionConfig: {
        ...kml
      },
      Folder: [{
        wpml_templateType: TEMPLATE_TYPE[kmlData.template_types[0]],
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: WaylineTypeMap.WGS84,
          wpml_heightMode: kmlData.heightMode,
          wpml_globalShootHeight: kmlData.globalHeight
        },
        wpml_autoFlightSpeed: kmlData.autoFlightSpeed,
        Placemark: [{
          wpml_caliFlightEnable: 0,
          wpml_elevationOptimizeEnable: 0,
          wpml_smartObliqueEnable: 0,
          wpml_quickOrthoMappingEnable: 0,
          wpml_facadeWaylineEnable: 0,
          wpml_isLookAtSceneSet: 0,
          wpml_shootType: kmlData.shootType,
          wpml_direction: 0,
          wpml_margin: kmlData.margin,
          wpml_efficiencyFlightModeEnable: 0,
          wpml_overlap: {
            wpml_orthoCameraOverlapH: kmlData.orthoLidarOverlapW,
            wpml_orthoCameraOverlapW: kmlData.orthoLidarOverlapW,
            wpml_inclinedCameraOverlapH: kmlData.orthoLidarOverlapW,
            wpml_inclinedCameraOverlapW: kmlData.orthoLidarOverlapW
          },
          Polygon: {
            outerBoundaryIs: {
              LinearRing: {
                coordinates: []
              }
            }
          },
          wpml_ellipsoidHeight: kmlData.globalHeight,
          wpml_height: kmlData.globalHeight
        }],
        wpml_payloadParam: {
          wpml_payloadPositionIndex: 0,
          wpml_focusMode: FOCUS_MODE_TYPE.FIRST_POINT,
          wpml_meteringMode: METERING_MODE_TYPE.AVERAGE,
          wpml_returnMode: RETURN_MODE_TYPE.SINGLE_RETURN_FIRST,
          wpml_samplingRate: 24e4,
          wpml_scanningMode: SCANNING_MODE_TYPE.REPETITIVE,
          wpml_imageFormat: kmlData.imageFormat.join()
        }
      }]
    }
    const actionGroup = this.setOther(kmlData.payload_types)
    const waylinesInfo = {
      wpml_missionConfig: {
        ...kml
      },
      Folder: [{
        wpml_templateId: 0,
        wpml_executeHeightMode: WaylineTypeMap.WGS84,
        wpml_waylineId: 0,
        wpml_distance: kmlData.distance,
        wpml_duration: kmlData.duration,
        wpml_autoFlightSpeed: kmlData.autoFlightSpeed,
        wpml_startActionGroup: actionGroup,
        Placemark: []
      }]
    }
    return {
      templateInfo: wpml,
      waylinesInfo: waylinesInfo
    }
  }

  static setWaypointKml (kmlData:any) {
    const time = new Date().getTime()
    let heightModel
    if (kmlData.heightMode === WaylineMapType.RELATIVE_TO_START_POINT) {
      heightModel = WaylineMapType.RELATIVE_TO_START_POINT
    } else {
      heightModel = WaylineTypeMap.WGS84
    }
    let startPoint = null
    if (kmlData.startPoint) {
      startPoint = new GetNewPoint(kmlData.startPoint.latitude, kmlData.startPoint.longitude, kmlData.startPoint.height).toString()
    }
    return {
      wpml_author: kmlData.author,
      wpml_createTime: time,
      wpml_updateTime: time,
      wpml_missionConfig: {
        wpml_flyToWaylineMode: kmlData.flyToWaylineMode,
        wpml_finishAction: kmlData.finishAction,
        wpml_exitOnRCLost: ExecuteRCLostActionType.GO_CONTINUE,
        wpml_executeRCLostAction: BtnLoading.GO_BACK,
        wpml_takeOffSecurityHeight: kmlData.takeOffSecurityHeight,
        wpml_takeOffRefPoint: startPoint,
        wpml_takeOffRefPointAGLHeight: 0,
        wpml_globalTransitionalSpeed: 15,
        wpml_globalRTHHeight: kmlData.globalRTHHeight,
        wpml_droneInfo: {
          wpml_droneEnumValue: kmlData.drone_type,
          wpml_droneSubEnumValue: 0
        },
        wpml_payloadInfo: {
          wpml_payloadEnumValue: kmlData.payload_types,
          wpml_payloadSubEnumValue: 0,
          wpml_payloadPositionIndex: 0
        }
      },
      Folder: [{
        wpml_templateType: TEMPLATE_TYPE[kmlData.template_types[0]],
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: WaylineTypeMap.WGS84,
          wpml_heightMode: kmlData.heightMode
        },
        wpml_autoFlightSpeed: kmlData.autoFlightSpeed,
        wpml_globalHeight: kmlData.globalHeight,
        wpml_caliFlightEnable: BooleanEnum.FALSE,
        wpml_gimbalPitchMode: kmlData.gimbalPitchMode,
        wpml_globalWaypointHeadingParam: {
          wpml_waypointHeadingMode: kmlData.waypointHeadingMode,
          wpml_waypointHeadingAngle: 0,
          wpml_waypointPoiPoint: new GetNewPoint().toString(),
          wpml_waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
          wpml_waypointHeadingPoiIndex: 0
        },
        wpml_globalUseStraightLine: BooleanEnum.TRUE,
        wpml_globalWaypointTurnMode: kmlData.waypointTurnMode,
        Placemark: [],
        wpml_waylineId: 0,
        wpml_executeHeightMode: heightModel,
        wpml_distance: kmlData.distance,
        wpml_duration: kmlData.duration
      }]
    }
  }

  static async setKmzDoc (row) {
    // 创建ZIP (KMZ) 文件
    const zip = new JSZip()
    const folder = zip.folder('wpmz')
    const templateInfo = this.objectToTemplateXML(row, 'kml')
    const waylinesInfo = this.objectToWaylinesXML(row, 'kml')
    folder.file('template.kml', templateInfo)
    folder.file('waylines.wpml', waylinesInfo)
    // 生成KMZ文件
    const kmzBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 9
      }
    })
    // 生成KMZ文件
    return kmzBlob
  }

  static async setZipDoc (row) {
    // 创建ZIP (KMZ) 文件
    const zip = new JSZip()
    const folder = zip.folder('wpmz')
    const templateInfo = this.objectToXML(row.templateInfo, 'kml')
    const waylinesInfo = this.objectToXML(row.waylinesInfo, 'kml')
    folder.file('template.kml', templateInfo)
    folder.file('waylines.wpml', waylinesInfo)
    // 生成KMZ文件
    const kmzBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 9
      }
    })
    // 生成KMZ文件
    return kmzBlob
  }

  static async setPointDoc (kmlData, b?:any) {
    const zip = new JSZip()
    const folder = zip.folder('wpmz')
    if (kmlData !== null) {
      kmlData.Folder.forEach(row => {
        const list = row.Placemark
        if (list !== null) {
          list.forEach((item, index) => {
            if (index === 0 || index === row.Placemark.length - 1) {
              item.wpml_waypointTurnParam.wpml_waypointTurnDampingDist = 0
              item.wpml_waypointTurnParam.wpml_waypointTurnMode = WaylineSmoothType.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE
            } else if (item.wpml_useGlobalTurnParam === 1) {
              item.wpml_waypointTurnParam.wpml_waypointTurnMode === WaylineSmoothType.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE ? item.wpml_waypointTurnParam.wpml_waypointTurnDampingDist = 0 : item.wpml_waypointTurnParam.wpml_waypointTurnDampingDist = 10
            } else {
              if (item.wpml_waypointTurnParam.wpml_waypointTurnMode === WaylineSmoothType.COORDINATE_TURN) { return }
              item.wpml_waypointTurnParam.wpml_waypointTurnMode === WaylineSmoothType.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE ? item.wpml_waypointTurnParam.wpml_waypointTurnDampingDist = 0 : item.wpml_waypointTurnParam.wpml_waypointTurnDampingDist = 10
            }
          }
          )
        }
      }
      )
    }
    folder.file('template.kml', this.objectToXML(kmlData, 'kml'))

    const payloadParam = this.setParamsKml(kmlData)
    folder.file('waylines.wpml', this.objectToXML(payloadParam, 'kml'))
    const kmzBlob = await zip.generateAsync({
      compression: 'DEFLATE',
      type: 'blob'
    })
    return kmzBlob
  }

  static setParamsKml (kmlData) {
    if (kmlData !== null) {
      kmlData.Folder.forEach(item => {
        let payloadParam, Placemark
        const list = (payloadParam = item.wpml_payloadParam) == null ? undefined : payloadParam.wpml_imageFormat
        list && ((Placemark = item.Placemark) == null || Placemark.forEach(child => {
          let actionGroup;
          (actionGroup = child.wpml_actionGroup) == null || actionGroup.forEach(children => {
            let action;
            (action = children.wpml_action) == null || action.forEach(actions => {
              let actionActuatorFuncParam;
              ((actionActuatorFuncParam = actions.wpml_actionActuatorFuncParam) == null ? undefined : actionActuatorFuncParam.wpml_useGlobalPayloadLensIndex) === 1 && (actions.wpml_actionActuatorFuncParam.wpml_payloadLensIndex = list)
            }
            )
          }
          )
        }
        ))
      }
      )
    }
    return kmlData
  }

  /**
   * 生成平面航线KML/WPML
   * @param config 配置对象
   * @returns 包含templateInfo和waylinesInfo的对象
   */
  static generatePlanarRoute (config: MissionConfig): MissionResult {
    const templateInfo: MissionTemplateInfo = {
      wpml_author: 'DJI KML Tool',
      wpml_createTime: Date.now(),
      wpml_updateTime: Date.now(),
      wpml_missionConfig: {
        wpml_flyToWaylineMode: config.flyToWaylineMode,
        wpml_finishAction: config.finishAction,
        wpml_exitOnRCLost: ExitOnRCLost.GO_CONTINUE,
        wpml_executeRCLostAction: ExecuteRCLostAction.GO_BACK,
        wpml_takeOffSecurityHeight: config.takeOffSecurityHeight,
        wpml_globalTransitionalSpeed: 15,
        wpml_globalRTHHeight: 100,
        wpml_droneInfo: {
          wpml_droneEnumValue: config.droneEnumValue,
          wpml_droneSubEnumValue: 1
        },
        wpml_payloadInfo: {
          wpml_payloadEnumValue: config.payloadEnumValue,
          wpml_payloadSubEnumValue: 0,
          wpml_payloadPositionIndex: 0
        }
      },
      Folder: [{
        wpml_templateType: TemplateType.MAPPING_3D,
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: CoordinateMode.WGS84,
          wpml_heightMode: HeightMode.RELATIVE_TO_START_POINT,
          wpml_globalShootHeight: config.globalHeight
        },
        wpml_autoFlightSpeed: config.autoFlightSpeed,
        Placemark: [{
          Polygon: {
            outerBoundaryIs: {
              LinearRing: {
                coordinates: config.waypoints.map(wp => `${wp.longitude},${wp.latitude}`).join('\n')
              }
            }
          },
          wpml_ellipsoidHeight: config.globalHeight,
          wpml_height: config.globalHeight
        }],
        wpml_payloadParam: {
          wpml_payloadPositionIndex: 0,
          wpml_imageFormat: 'visable'
        }
      }]
    }

    const waylinesInfo: MissionWaylinesInfo = {
      wpml_missionConfig: templateInfo.wpml_missionConfig,
      Folder: [{
        wpml_templateId: 0,
        wpml_executeHeightMode: CoordinateMode.WGS84,
        wpml_waylineId: 0,
        wpml_distance: this.calculateTotalDistance(config.waypoints),
        wpml_duration: this.calculateTotalDuration(config.waypoints, config.autoFlightSpeed),
        wpml_autoFlightSpeed: config.autoFlightSpeed,
        Placemark: config.waypoints.map((wp, index) => ({
          Point: {
            coordinates: `${wp.longitude},${wp.latitude}`
          },
          wpml_index: index,
          wpml_executeHeight: wp.altitude,
          wpml_waypointSpeed: config.autoFlightSpeed,
          wpml_waypointHeadingParam: {
            wpml_waypointHeadingMode: index === 0
              ? WaypointHeadingMode.FOLLOW_WAYLINE
              : WaypointHeadingMode.FIXED,
            wpml_waypointHeadingAngle: 0
          },
          wpml_waypointTurnParam: {
            wpml_waypointTurnMode: index === 0 || index === config.waypoints.length - 1
              ? WaypointTurnMode.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE
              : WaypointTurnMode.COORDINATE_TURN,
            wpml_waypointTurnDampingDist: index === 0 || index === config.waypoints.length - 1 ? 0 : 0.2
          }
        }))
      }]
    }

    return {
      templateInfo: templateInfo,
      waylinesInfo: waylinesInfo
    }
  }

  /**
   * 计算总距离（简化版）
   * @param waypoints 航点数组
   * @returns 总距离（米）
   */
  static calculateTotalDistance (waypoints: Waypoint[]): string {
    let totalDistance = 0
    for (let i = 0; i < waypoints.length - 1; i++) {
      const wp1 = waypoints[i]
      const wp2 = waypoints[i + 1]
      // 简化计算，实际应使用Haversine公式
      const distance = Math.sqrt(
        Math.pow(wp2.longitude - wp1.longitude, 2) +
        Math.pow(wp2.latitude - wp1.latitude, 2)
      ) * 111000 // 近似转换为米
      totalDistance += distance
    }
    return totalDistance.toFixed(2)
  }

  /**
   * 计算总时长
   * @param waypoints 航点数组
   * @param speed 飞行速度（米/秒）
   * @returns 总时长（秒）
   */
  static calculateTotalDuration (waypoints: Waypoint[], speed: number): string {
    const totalDistance = parseFloat(this.calculateTotalDistance(waypoints))
    return (totalDistance / speed).toFixed(2)
  }

  /**
   * 格式化JSON输出
   * @param obj JavaScript对象
   * @returns 格式化的JSON字符串
   */
  static formatJSON (obj: any): string {
    return JSON.stringify(obj, null, 2)
  }

  /**
   * 下载文件
   * @param content 文件内容
   * @param filename 文件名
   * @param type MIME类型
   */
  static downloadFile (content: string, filename: string, type: string): void {
    const blob = new Blob([content], { type: type })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()

    URL.revokeObjectURL(url)
  }

  static objectToTemplateXML (obj:any, rootElement: string = 'kml'):string {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${rootElement} xmlns="http://www.opengis.net/kml/2.2" xmlns:wpml="http://www.dji.com/wpmz/1.0.6">\n<Document>\n`
    const row = JSON.parse(JSON.stringify(obj))
    const xmlObj = {
      wpml_author: row.wpml_author,
      wpml_createTime: row.wpml_createTime,
      wpml_updateTime: row.wpml_updateTime,
      wpml_missionConfig: {
        wpml_flyToWaylineMode: row.wpml_missionConfig.wpml_flyToWaylineMode,
        wpml_finishAction: row.wpml_missionConfig.wpml_finishAction,
        wpml_exitOnRCLost: row.wpml_missionConfig.wpml_exitOnRCLost,
        wpml_executeRCLostAction: row.wpml_missionConfig.wpml_executeRCLostAction,
        wpml_takeOffSecurityHeight: row.wpml_missionConfig.wpml_takeOffSecurityHeight,
        wpml_takeOffRefPoint: row.wpml_missionConfig.wpml_takeOffRefPoint,
        wpml_takeOffRefPointAGLHeight: row.wpml_missionConfig.wpml_takeOffRefPointAGLHeight,
        wpml_globalTransitionalSpeed: row.wpml_missionConfig.wpml_globalTransitionalSpeed,
        wpml_globalRTHHeight: row.wpml_missionConfig.wpml_globalRTHHeight,
        wpml_droneInfo: row.wpml_missionConfig.wpml_droneInfo,
        wpml_payloadInfo: row.wpml_missionConfig.wpml_payloadInfo,
        wpml_autoRerouteInfo: row.wpml_missionConfig.wpml_autoRerouteInfo
      },
      Folder: row.Folder.map((item) => {
        return {
          wpml_templateType: item.wpml_templateType,
          wpml_templateId: item.wpml_templateId,
          wpml_waylineCoordinateSysParam: {
            wpml_coordinateMode: item.wpml_waylineCoordinateSysParam.wpml_coordinateMode,
            wpml_heightMode: item.wpml_waylineCoordinateSysParam.wpml_heightMode,
          },
          wpml_autoFlightSpeed: item.wpml_autoFlightSpeed,
          wpml_globalHeight: item.wpml_globalHeight,
          wpml_caliFlightEnable: item.wpml_caliFlightEnable,
          wpml_gimbalPitchMode: item.wpml_gimbalPitchMode,
          wpml_globalWaypointHeadingParam: {
            wpml_waypointHeadingMode: item.wpml_globalWaypointHeadingParam.wpml_waypointHeadingMode,
            wpml_waypointHeadingAngle: item.wpml_globalWaypointHeadingParam.wpml_waypointHeadingAngle,
            wpml_waypointPoiPoint: item.wpml_globalWaypointHeadingParam.wpml_waypointPoiPoint,
            wpml_waypointHeadingPathMode: item.wpml_globalWaypointHeadingParam.wpml_waypointHeadingPathMode,
            wpml_waypointHeadingPoiIndex: item.wpml_globalWaypointHeadingParam.wpml_waypointHeadingPoiIndex
          },
          wpml_globalUseStraightLine: item.wpml_globalUseStraightLine,
          wpml_globalWaypointTurnMode: item.wpml_globalWaypointTurnMode,
          wpml_payloadParam: item.wpml_payloadParam,
          Placemark: item.Placemark.map((row) => {
            return {
              Point: row.Point,
              wpml_index: row.wpml_index,
              wpml_ellipsoidHeight: row.wpml_ellipsoidHeight,
              wpml_height: row.wpml_height,
              wpml_waypointSpeed: row.wpml_waypointSpeed,
              wpml_waypointHeadingParam: {
                wpml_waypointHeadingMode: row.wpml_waypointHeadingParam.wpml_waypointHeadingMode,
                wpml_waypointHeadingPoiIndex: row.wpml_waypointHeadingParam.wpml_waypointHeadingPoiIndex,
                wpml_waypointHeadingAngle: row.wpml_waypointHeadingParam.wpml_waypointHeadingAngle,
                wpml_waypointPoiPoint: row.wpml_waypointHeadingParam.wpml_waypointPoiPoint,
                wpml_waypointHeadingPathMode: row.wpml_waypointHeadingParam.wpml_waypointHeadingPathMode,
              },
              wpml_waypointTurnParam: {
                wpml_waypointTurnMode: row.wpml_waypointTurnParam.wpml_waypointTurnMode,
                wpml_waypointTurnDampingDist: row.wpml_waypointTurnParam.wpml_waypointTurnDampingDist,
              },
              wpml_useGlobalHeight: row.wpml_useGlobalHeight,
              wpml_useGlobalSpeed: row.useGlobalSpeed || 1,
              wpml_useGlobalHeadingParam: row.wpml_useGlobalHeadingParam,
              wpml_useGlobalTurnParam: row.wpml_useGlobalTurnParam,
              wpml_useStraightLine: row.wpml_useStraightLine,
              wpml_actionGroup: row.wpml_actionGroup,
              wpml_isRisky: row.wpml_isRisky,

            }
          })

        }
      })

    }
    const addToXML = (data: any, indent: string = '  ') => {
      for (let key in data) {
        // 使用安全的 hasOwnProperty 检查
        if (hasOwnProperty(data, key)) {
          const value = data[key]
          key = key.replace('wpml_', 'wpml:')
          if (Array.isArray(value)) {
            // 处理数组
            for (const item of value) {
              xml += `${indent}<${key}>\n`
              addToXML(item, indent + '  ')
              xml += `${indent}</${key}>\n`
            }
          } else if (typeof value === 'object' && value !== null) {
            // 处理对象
            xml += `${indent}<${key}>\n`
            addToXML(value, indent + '  ')
            xml += `${indent}</${key}>\n`
          } else {
            // 处理基本类型
            xml += `${indent}<${key}>${value}</${key}>\n`
          }
        }
      }
    }
    addToXML(xmlObj)
    xml += `</Document></${rootElement}>`
    return xml
  }

  static objectToWaylinesXML (obj: any, rootElement: string = 'kml'): string {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${rootElement} xmlns="http://www.opengis.net/kml/2.2" xmlns:wpml="http://www.dji.com/wpmz/1.0.6">\n<Document>\n`
    const row = JSON.parse(JSON.stringify(obj))
    const xmlObj = {
      wpml_missionConfig: {
        wpml_flyToWaylineMode: row.wpml_missionConfig.wpml_flyToWaylineMode,
        wpml_finishAction: row.wpml_missionConfig.wpml_finishAction,
        wpml_exitOnRCLost: row.wpml_missionConfig.wpml_exitOnRCLost,
        wpml_executeRCLostAction: row.wpml_missionConfig.wpml_executeRCLostAction,
        wpml_takeOffSecurityHeight: row.wpml_missionConfig.wpml_takeOffSecurityHeight,
        wpml_globalTransitionalSpeed: row.wpml_missionConfig.wpml_globalTransitionalSpeed,
        wpml_globalRTHHeight: row.wpml_missionConfig.wpml_globalRTHHeight,
        wpml_droneInfo: row.wpml_missionConfig.wpml_droneInfo,
        wpml_payloadInfo: row.wpml_missionConfig.wpml_payloadInfo
      },
      Folder: row.Folder.map(el => {
        return {
          wpml_templateId: el.wpml_templateId,
          wpml_executeHeightMode: el.wpml_executeHeightMode,
          wpml_autoFlightSpeed: el.wpml_autoFlightSpeed,
          wpml_waylineId: el.wpml_waylineId,
          wpml_distance: el.wpml_distance,
          wpml_duration: el.wpml_duration,
          Placemark: el.Placemark.map(element => {
            return {
              Point: element.Point,
              wpml_index: element.wpml_index,
              wpml_ellipsoidHeight: element.wpml_ellipsoidHeight,
              wpml_height: element.wpml_height,
              wpml_executeHeight: element.wpml_executeHeight,
              wpml_waypointSpeed: element.wpml_waypointSpeed,
              wpml_waypointHeadingParam: element.wpml_waypointHeadingParam,
              wpml_waypointTurnParam: element.wpml_waypointTurnParam,
              wpml_useStraightLine: element.wpml_useStraightLine,
              wpml_actionGroup: element.wpml_actionGroup,
              wpml_waypointGimbalHeadingParam: element.wpml_waypointGimbalHeadingParam,
              wpml_isRisky: element.wpml_isRisky,
              wpml_waypointWorkType: element.wpml_waypointWorkType,

            }
          }),

        }
      })
    }
    const addToXML = (data: any, indent: string = '  ') => {
      for (let key in data) {
        // 使用安全的 hasOwnProperty 检查
        if (hasOwnProperty(data, key)) {
          const value = data[key]
          key = key.replace('wpml_', 'wpml:')
          if (Array.isArray(value)) {
            // 处理数组
            for (const item of value) {
              xml += `${indent}<${key}>\n`
              addToXML(item, indent + '  ')
              xml += `${indent}</${key}>\n`
            }
          } else if (typeof value === 'object' && value !== null) {
            // 处理对象
            xml += `${indent}<${key}>\n`
            addToXML(value, indent + '  ')
            xml += `${indent}</${key}>\n`
          } else {
            // 处理基本类型
            xml += `${indent}<${key}>${value}</${key}>\n`
          }
        }
      }
    }
    addToXML(xmlObj)
    xml += `</Document></${rootElement}>`
    return xml
  }

  /**
   * 将KML/WPML对象转换为XML字符串
   * @param obj KML/WPML对象
   * @param rootElement 根元素名称
   * @returns XML字符串
   */
  static objectToXML (obj: any, rootElement: string = 'kml'): string {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${rootElement} xmlns="http://www.opengis.net/kml/2.2" xmlns:wpml="http://www.dji.com/wpmz/1.0.6">\n<Document>\n`

    const addToXML = (data: any, indent: string = '  ') => {
      for (let key in data) {
        // 使用安全的 hasOwnProperty 检查
        if (hasOwnProperty(data, key)) {
          const value = data[key]
          if ((key === 'wpml_waypointSpeed' || key === 'wpml_executeHeight') && !value) return
          key = key.replace('wpml_', 'wpml:')
          if (Array.isArray(value)) {
            // 处理数组
            for (const item of value) {
              xml += `${indent}<${key}>\n`
              addToXML(item, indent + '  ')
              xml += `${indent}</${key}>\n`
            }
          } else if (typeof value === 'object' && value !== null) {
            // 处理对象
            xml += `${indent}<${key}>\n`
            addToXML(value, indent + '  ')
            xml += `${indent}</${key}>\n`
          } else {
            // 处理基本类型
            xml += `${indent}<${key}>${value}</${key}>\n`
          }
        }
      }
    }

    addToXML(obj)
    xml += `</Document></${rootElement}>`

    return xml
  }

  /**
   * 将XML字符串转换为KML/WPML对象
   * @param xml XML字符串
   * @returns 解析后的对象
   */
  static xmlToObject (xml: string): any {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xml, 'text/xml')

    const parseNode = (node: Element): any => {
      const obj: any = {}

      // 处理属性
      if (node.attributes.length > 0) {
        obj.attributes = {}
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i]
          obj.attributes[attr.name] = attr.value
        }
      }

      // 处理子节点
      for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i] as Element

        // 跳过文本节点
        if (child.nodeType !== 1) continue

        const childName = child.nodeName.replace('wpml:', '')
        const childValue = parseNode(child)

        // 如果已经有同名属性，转换为数组
        // 使用安全的 hasOwnProperty 检查
        if (hasOwnProperty(obj, childName)) {
          if (!Array.isArray(obj[childName])) {
            obj[childName] = [obj[childName]]
          }
          obj[childName].push(childValue)
        } else {
          obj[childName] = childValue
        }
      }

      // 如果没有子节点，返回文本内容
      if (Object.keys(obj).length === 0) {
        return node.textContent || ''
      }

      return obj
    }

    return parseNode(xmlDoc.documentElement)
  }
}

export default DJIWaylineTools
